import React from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Chip,
    Button,
    Avatar,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Waves, Fish, Leaf, Award, MapPin, Users, Shield, Droplets } from 'lucide-react';

// Styled components
const HeroSection = styled(Box)(({ theme }) => ({
    position: 'relative',
    minHeight: '80vh',
    background: 'linear-gradient(135deg, #4A602F 0%, #0d9488 50%, #4A602F 100%)',
    display: 'flex',
    alignItems: 'center',
    color: 'white',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        zIndex: 1,
    },
}));

const WaveBackground = styled('svg')({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0.2,
    zIndex: 0,
});

const HeroContent = styled(Box)({
    position: 'relative',
    zIndex: 2,
    textAlign: 'center',
});

const GradientCard = styled(Card)(({ theme, bgcolor }) => ({
    background: bgcolor || 'linear-gradient(135deg, #f0f9ff 0%, #e6fffa 100%)',
    border: `1px solid #4A602F`,
    borderRadius: 16,
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[8],
    },
}));

const InfoChip = styled(Chip)({
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    '& .MuiChip-icon': {
        color: 'white',
    },
});

const FishImageContainer = styled(Box)({
    position: 'relative',
    borderRadius: 20,
    overflow: 'hidden',
    height: 400,
    backgroundImage: 'url("/assets/fishimg.jpeg")',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(74, 96, 47, 0.8) 0%, rgba(13, 148, 136, 0.8) 100%)',
        zIndex: 1,
    },
});

const AquacultureDivision = () => {
    const fishProducts = [
        { name: "Rohu (Labeo rohita)", description: "Rich in protein, widely loved across India" },
        { name: "Catla", description: "Mild flavor and flaky texture" },
        { name: "Tilapia", description: "Fast-growing and affordable" },
        { name: "Freshwater Prawns & Shrimps", description: "Premium, clean-farmed" }
    ];

    const dams = ["Marnewadi Dam", "Temghar Dam", "Urvade Dam"];

    const products = [
        "Live Fish (bulk supply)",
        "Fresh, Cleaned & Packed Fish (retail orders)",
        "Fish Fingerlings (for other farms)"
    ];

    const practices = [
        "Bio-secure ponds with controlled feeding",
        "Regular water quality testing",
        "No antibiotics or growth hormones",
        "Use of probiotic-based biofloc systems",
        "Training and employment for rural youth"
    ];

    const certifications = ["FSSAI Licensed", "Organic Aquaculture (in progress)", "MSME Registered"];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'linear-gradient(135deg, #f0f9ff 0%, #e6fffa 50%, #f0fdf4 100%)' }}>
            <HeroSection>
                <WaveBackground viewBox="0 0 100 100" preserveAspectRatio="none">
                    <path d="M0,50 Q25,30 50,50 T100,50 L100,100 L0,100 Z" fill="url(#wave)" />
                    <defs>
                        <linearGradient id="wave" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#4A602F" stopOpacity="0.3" />
                            <stop offset="50%" stopColor="#0d9488" stopOpacity="0.5" />
                            <stop offset="100%" stopColor="#4A602F" stopOpacity="0.3" />
                        </linearGradient>
                    </defs>
                </WaveBackground>

                <Container maxWidth="lg">
                    <HeroContent>
                        <Avatar sx={{
                            width: 80,
                            height: 80,
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            mx: 'auto',
                            mb: 3,
                            backdropFilter: 'blur(10px)'
                        }}>
                            <Fish size={40} />
                        </Avatar>

                        <Typography
                            variant="h2"
                            component="h1"
                            sx={{
                                fontWeight: 'bold',
                                mb: 2,
                                background: 'linear-gradient(45deg, #ffffff 30%, #e0f2fe 90%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                textAlign: 'center'
                            }}
                        >
                            West Valley Aquaculture Division
                        </Typography>

                        <Typography variant="h5" sx={{ mb: 4, color: '#bfdbfe', maxWidth: 800, mx: 'auto' }}>
                            Sustainable Fish Farming for a Healthier Tomorrow
                        </Typography>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
                            <InfoChip icon={<MapPin size={16} />} label="Western Maharashtra" />
                            <InfoChip icon={<Waves size={16} />} label="1,000+ Hectares" />
                            <InfoChip icon={<Leaf size={16} />} label="Eco-Friendly" />
                        </Box>
                    </HeroContent>
                </Container>
            </HeroSection>

            {/* About Section */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} lg={6}>
                        <Typography variant="h3" component="h2" sx={{ fontWeight: 'bold', mb: 3, color: '#1f2937' }}>
                            About Us
                        </Typography>

                        <Typography variant="h6" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.7 }}>
                            West Valley Organic Farm aquaculture division is a leading aquaculture enterprise located in western Maharashtra.
                            We are engaged in fish farming across nearly 1,000 hectares of aquaculture area, specializing in sustainable
                            fish farming founded with a vision to support food security and rural livelihoods.
                        </Typography>

                        <Typography variant="body1" sx={{ mb: 4, color: '#6b7280', lineHeight: 1.6 }}>
                            We have grown into a trusted name in freshwater aquaculture, where innovation meets nature to deliver the
                            freshest, healthiest fish from farm to fork. Our eco-friendly aquaculture practices ensure high-quality
                            fish production with minimal environmental impact.
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <GradientCard bgcolor="linear-gradient(135deg, #dbeafe 0%, #e6fffa 100%)">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Shield size={20} color="#4A602F" />
                                            <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold', color: '#4A602F' }}>
                                                Our Mission
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#374151' }}>
                                            To provide healthy, chemical-free fish to consumers while promoting sustainable farming practices
                                            that respect both people and the planet.
                                        </Typography>
                                    </CardContent>
                                </GradientCard>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <GradientCard bgcolor="linear-gradient(135deg, #f0fdf4 0%, #e6fffa 100%)">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Leaf size={20} color="#4A602F" />
                                            <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold', color: '#4A602F' }}>
                                                Our Vision
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#374151' }}>
                                            To become a model for eco-friendly aquaculture across India, delivering value through
                                            quality, integrity, and innovation.
                                        </Typography>
                                    </CardContent>
                                </GradientCard>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} lg={6}>
                        <FishImageContainer>
                            <Box sx={{ position: 'relative', zIndex: 2, color: 'white', textAlign: 'center', p: 4 }}>
                                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
                                    Premium Aquaculture
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Droplets size={20} style={{ marginRight: 12, color: '#bfdbfe' }} />
                                        <Typography>Chemical-free farming</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Users size={20} style={{ marginRight: 12, color: '#bfdbfe' }} />
                                        <Typography>Rural employment generation</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Award size={20} style={{ marginRight: 12, color: '#bfdbfe' }} />
                                        <Typography>Certified quality standards</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </FishImageContainer>
                    </Grid>
                </Grid>
            </Container>

            {/* Fish Products Section */}
            <Box sx={{ bgcolor: 'white', py: 8 }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" component="h2" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 6, color: '#1f2937' }}>
                        Our Fish & Products We Specialize In
                    </Typography>
                    <Grid container spacing={3}>
                        {fishProducts.map((fish, index) => (
                            <Grid item xs={12} sm={6} lg={3} key={index}>
                                <GradientCard>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Fish size={24} color="#4A602F" />
                                            <Typography variant="h6" sx={{ ml: 2, fontWeight: 'bold', color: '#1f2937' }}>
                                                {fish.name}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                            {fish.description}
                                        </Typography>
                                    </CardContent>
                                </GradientCard>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Dam Reservoirs & Products */}
            <Box sx={{ bgcolor: 'linear-gradient(135deg, #e6fffa 0%, #dbeafe 100%)', py: 8 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6}>
                        <Grid item xs={12} lg={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                                <Waves size={32} color="#4A602F" />
                                <Typography variant="h4" sx={{ ml: 2, fontWeight: 'bold', color: '#1f2937' }}>
                                    Our Dam Reservoirs
                                </Typography>
                            </Box>
                            <List>
                                {dams.map((dam, index) => (
                                    <ListItem key={index} sx={{
                                        bgcolor: 'white',
                                        mb: 2,
                                        borderRadius: 2,
                                        border: '1px solid #dbeafe',
                                        '&:hover': { bgcolor: '#f0f9ff' }
                                    }}>
                                        <ListItemIcon>
                                            <MapPin size={20} color="#4A602F" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={dam}
                                            primaryTypographyProps={{ fontWeight: 'medium', color: '#1f2937' }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>

                        <Grid item xs={12} lg={6}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 4, color: '#1f2937' }}>
                                Products
                            </Typography>
                            <List>
                                {products.map((product, index) => (
                                    <ListItem key={index} sx={{
                                        bgcolor: 'white',
                                        mb: 2,
                                        borderRadius: 2,
                                        border: '1px solid #dcfce7',
                                        '&:hover': { bgcolor: '#f0fdf4' }
                                    }}>
                                        <ListItemIcon>
                                            <Box sx={{
                                                width: 12,
                                                height: 12,
                                                bgcolor: '#4A602F',
                                                borderRadius: '50%',
                                                mr: 2
                                            }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={product}
                                            primaryTypographyProps={{ color: '#1f2937' }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Farming Practices */}
            <Box sx={{ bgcolor: 'white', py: 8 }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" component="h2" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 2, color: '#1f2937' }}>
                        Farming Practices
                    </Typography>
                    <Typography variant="h5" sx={{ textAlign: 'center', mb: 6, color: '#6b7280' }}>
                        Our Responsible Approach
                    </Typography>

                    <Grid container spacing={3}>
                        {practices.map((practice, index) => (
                            <Grid item xs={12} md={6} lg={4} key={index}>
                                <GradientCard bgcolor="linear-gradient(135deg, #f0fdf4 0%, #e6fffa 100%)">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                            <Leaf size={20} color="#4A602F" style={{ marginRight: 12, marginTop: 4, flexShrink: 0 }} />
                                            <Typography sx={{ color: '#1f2937' }}>
                                                {practice}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </GradientCard>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Certifications */}
            <Box sx={{
                background: 'linear-gradient(135deg, #4A602F 0%, #0d9488 100%)',
                py: 8,
                color: 'white'
            }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" component="h2" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 6 }}>
                        Certifications
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 3 }}>
                        {certifications.map((cert, index) => (
                            <Chip
                                key={index}
                                icon={<Award size={20} />}
                                label={cert}
                                sx={{
                                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                                    color: 'white',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    py: 2,
                                    px: 1,
                                    '& .MuiChip-icon': {
                                        color: 'white',
                                    },
                                }}
                            />
                        ))}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default AquacultureDivision;